<script setup>
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { scheduleApi } from '@/apis/schedule.api'
import { useAllocationAvailability } from '@/composables/allocation-availability'
import { useDetectDevice } from '@/composables/device'

const { t } = useI18n()
const { isMobile } = useDetectDevice()
const { loadAvailabilityAtProjectStart, buildAllocationsFromAvailability, fetchWarehouseNamesById } = useAllocationAvailability()

const props = defineProps({
  modelValue: { type: Boolean, required: true },
  serviceId: { type: Number, required: true },
  projectId: { type: Number, required: true },
  budgetDocumentRowId: { type: Number, default: null },
  serviceName: { type: String, default: '' },
  quantity: { type: Number, default: 1 },
  projectStart: { type: [Date, String], default: null },
})

const emit = defineEmits(['update:modelValue', 'confirmed'])

const isLoading = ref(false)
const isSaving = ref(false)
const warehouseStocks = ref([])
const warehouseAllocations = ref([])

const allocateQuantity = ref(props.quantity)

const visible = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
})

onMounted(async () => {
  isLoading.value = true
  try {
    const warehouseNamesById = await fetchWarehouseNamesById()
    const availability = await loadAvailabilityAtProjectStart([props.serviceId], props.projectStart)
    const allocations = buildAllocationsFromAvailability(availability, props.serviceId, warehouseNamesById)
    warehouseStocks.value = allocations
    warehouseAllocations.value = allocations.map(a => ({
      ...a,
      quantity: allocations.length === 1 ? Math.min(allocateQuantity.value, a.available) : 0,
    }))
  } finally {
    isLoading.value = false
  }
})

const hasStock = computed(() => warehouseStocks.value.length > 0)

const totalAllocated = computed(() =>
  warehouseAllocations.value.reduce((sum, a) => sum + (Number(a.quantity) || 0), 0),
)

const totalAvailableStock = computed(() =>
  warehouseAllocations.value.reduce((sum, a) => sum + (Number(a.available) || 0), 0),
)

const isValid = computed(() =>
  totalAllocated.value === allocateQuantity.value && totalAllocated.value <= totalAvailableStock.value,
)

const onConfirm = async () => {
  isSaving.value = true
  try {
    const allocations = warehouseAllocations.value
      .filter(a => Number(a.quantity) > 0)
      .map(a => ({ warehouse_id: a.warehouseId, quantity: Number(a.quantity) }))
    await scheduleApi.confirmAllocationAsync(props.serviceId, props.projectId, allocations, props.budgetDocumentRowId)
    emit('confirmed')
    visible.value = false
  } catch (err) {
    console.error('Failed to confirm allocation', err)
  } finally {
    isSaving.value = false
  }
}

const onCancel = () => {
  visible.value = false
}

</script>

<template>
  <q-dialog
    v-model="visible"
    :maximized="isMobile"
    persistent
    class="dialog-xs"
  >
    <q-card>
      <q-card-section>
        <div class="text-h6">{{ t('common.confirmAllocation') }}</div>
        <div class="text-caption text-grey-7">{{ props.serviceName }}</div>
      </q-card-section>

      <q-card-section>
        <q-spinner
          v-if="isLoading"
          color="primary"
          size="2em"
          class="q-mx-auto block"
        />

        <!-- No stock available -->
        <div
          v-else-if="!hasStock"
          class="text-grey-7"
        >
          {{ t('common.noStockAvailable') }}
        </div>

        <!-- Standard allocation: single/multi warehouse -->
        <template v-else>
          <div class="text-body2 q-mb-sm">{{ t('common.confirmAllocationMessage') }}</div>

          <div class="row items-center q-gutter-sm q-mb-sm">
            <div class="col">
              <q-input
                v-model.number="allocateQuantity"
                type="number"
                :min="1"
                :max="quantity"
                :label="t('common.quantityToAllocate')"
                :rules="[val => val >= 1 || t('common.required'), val => val <= quantity || `Max ${quantity}`]"
                outlined
                dense
              />
            </div>
          </div>
          <div class="text-caption text-grey-7 q-mb-md">
            {{ t('common.serviceStatuses.available') }}: {{ totalAvailableStock }}
          </div>

          <div
            v-if="warehouseAllocations.length > 1"
            class="text-subtitle2 q-mb-sm"
          >
            {{ t('common.selectWarehouseQuantities') }}
          </div>

          <div
            v-for="alloc in warehouseAllocations"
            :key="alloc.warehouseId"
            class="row items-center q-gutter-sm q-mb-sm"
          >
            <div class="col">
              <span class="text-weight-medium">{{ alloc.warehouseName }}</span>
            </div>
            <div class="col-auto text-caption text-grey-6">
              {{ t('common.serviceStatuses.available') }}: {{ alloc.available }}
            </div>
            <div class="col-3">
              <q-input
                v-model.number="alloc.quantity"
                type="number"
                :min="0"
                :max="alloc.available"
                dense
                outlined
                hide-bottom-space
              />
            </div>
          </div>

          <div
            class="text-caption"
            :class="totalAllocated !== allocateQuantity ? 'text-negative' : 'text-grey-7'"
          >
            {{ t('common.total') }}: {{ totalAllocated }} / {{ allocateQuantity }}
          </div>
        </template>
      </q-card-section>

      <q-card-actions align="right">
        <q-btn
          flat
          :label="t('common.cancel')"
          @click="onCancel"
        />
        <q-btn
          flat
          color="primary"
          :label="t('common.confirm')"
          :disable="!isValid || isLoading"
          :loading="isSaving"
          @click="onConfirm"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
